import {
  minLength,
  required,
  maxLength,
  requiredIf,
} from "@vuelidate/validators";
import type { ValidationArgs } from "@vuelidate/core";
import {
  houseNumberValidator,
  letterNumberValidator,
  letterSpecialValidator,
  atLeastOneNumeric,
  atLeastOneNonNumeric,
} from "./customValidators/customValidators";
import { useCountriesStore } from "~/stores/countriesstore";

export interface CreateCompanyForm {
  firstName: string;
  lastName: string;
  phoneNumber: string;

  companyName: string;
  country: string;
  vatIdNumber: string;
  pxcNumber: string;

  additionalInfo: string;
  street: string;
  houseNumber: string;
  postalCode: string;
  city: string;
  state: string;
  province: string;
}

export function getCreateCompanyFormRules(
  createCompanyForm: CreateCompanyForm,
): ValidationArgs<CreateCompanyForm> {
  const countriesStore = useCountriesStore();
  const selectedCountry = countriesStore.countries.find(
    (country) => country.iso === createCompanyForm.country,
  );

  const isUsHub = computed(
    () =>
      selectedCountry?.iso === "US" ||
      selectedCountry?.iso === "CA" ||
      selectedCountry?.iso === "MX",
  );

  return {
    firstName: { required, minLength: minLength(2), maxLength: maxLength(30) },
    lastName: { required, minLength: minLength(2), maxLength: maxLength(30) },
    phoneNumber: {
      required,
      minLength: minLength(5),
      maxLength: maxLength(25),
      phoneNumberChecker: function (value) {
        if (value) {
          return /^([+\d]|-| )*$/.test(value);
        } else {
          return true;
        }
      },
    },
    companyName: {
      required,
      minLength: minLength(2),
      maxLength: maxLength(50),
    },
    country: { required },
    additionalInfo: { maxLength: maxLength(500) },

    vatIdNumber: {
      required: requiredIf(computed(() => selectedCountry?.status === "Full")),
      minLength: minLength(5),
      maxLength: maxLength(30),
      vatIdChecker: function (value) {
        if (value === "vatNonWhiteListed") {
          return true;
        } else if (value) {
          return /^[A-Za-z]{2,4}(?=.{2,12}$)[-_\s0-9]*(?:[a-zA-Z][-_\s0-9]*){0,2}$/.test(
            value,
          );
        } else {
          return true;
        }
      },
    },
    pxcNumber: {},

    street: {
      required,
      minLength: minLength(2),
      maxLength: maxLength(30),
      atLeastOneNonNumeric,
    },
    houseNumber: {
      required: requiredIf(!isUsHub.value),
      houseNumberValidator: (s) => {
        if (!isUsHub.value) {
          return houseNumberValidator(s);
        }
        return true;
      },
      atLeastOneNumeric: (s) => {
        if (!isUsHub.value) {
          return atLeastOneNumeric(s);
        }
        return true;
      },
    },
    postalCode: {
      required,
      letterNumberValidator,
      minLength: minLength(4),
      maxLength: maxLength(10),
      atLeastOneNumeric,
    },
    city: {
      required,
      letterSpecialValidator,
      minLength: minLength(3),
      maxLength: maxLength(30),
    },
    state: {
      requiredIf: requiredIf(
        computed(
          () => selectedCountry?.iso === "US" || selectedCountry?.iso === "MX",
        ),
      ),
    },
    province: {
      requiredIf: requiredIf(computed(() => selectedCountry?.iso === "CA")),
    },
  };
}

export const validationMessages: Record<keyof CreateCompanyForm, object> = {
  firstName: {
    required: "Name is required",
    minLength: "Name should be at least 2 characters long",
    maxLength: "Name can be maximum 30 characters long",
  },
  lastName: {
    required: "Last name is required",
    minLength: "Last name should be at least 2 characters long",
    maxLength: "Last name can be maximum 30 characters long",
  },
  phoneNumber: {
    required: "Phone number is required",
    minLength: "Phone number should be at least 5 characters long",
    maxLength: "Phone number can be maximum 25 characters long",
    phoneNumberChecker: "Please enter a valid phone number",
  },
  companyName: {
    required: "Company name is required",
    minLength: "Company name should be at least 2 characters long",
    maxLength: "Company name can be maximum 50 characters long",
  },
  country: {
    required: "Country is required",
  },
  vatIdNumber: {
    required: "VAT ID Number is required",
    minLength: "VAT ID should be at least 5 characters long",
    maxLength: "VAT ID can be maximum 30 characters long",
    vatIdChecker: "Please enter a valid VAT ID",
  },
  pxcNumber: {},
  additionalInfo: {
    maxLength: "Additional info can be maximum 500 characters long",
  },
  street: {
    required: "Street is required",
    minLength: "Street should be at least 2 characters long",
    maxLength: "Street can be maximum 30 characters long",
    atLeastOneNonNumeric: "Street must contain at least one letter",
  },
  houseNumber: {
    required: "House number is required",
    houseNumberValidator: "Please enter a valid house number",
    atLeastOneNumeric: "House number must contain at least one digit",
  },
  postalCode: {
    required: "Postal code is required",
    letterNumberValidator: "Please enter a valid postal code",
    minLength: "Postal code should be at least 4 characters long",
    maxLength: "Postal code can be maximum 10 characters long",
    atLeastOneNumeric: "Postal code must contain at least one digit",
  },
  city: {
    required: "City is required",
    letterSpecialValidator: "Please enter a valid city name",
    minLength: "City should be at least 3 characters long",
    maxLength: "City can be maximum 30 characters long",
  },
  state: {
    requiredIf: "State is required.",
  },
  province: {
    requiredIf: "Province is required.",
  },
};

// Map of field names to more user-friendly labels
export const fieldLabelsMap: Record<keyof CreateCompanyForm, string> = {
  firstName: "First Name",
  lastName: "Last Name",
  phoneNumber: "Phone Number",
  companyName: "Company Name",
  country: "Country",
  vatIdNumber: "VAT ID Number",
  pxcNumber: "PXC Number",
  additionalInfo: "Additional Info",
  street: "Street",
  houseNumber: "House Number",
  postalCode: "Postal Code",
  city: "City",
  state: "State",
  province: "Province",
};
